'use strict';

import { Stack } from 'swing';
import ClassLogger from 'ClassLogger';

class SongVoting {
    /**
     * Returns the class name used by the ClassLogger.
     *
     * @returns {string}
     */
    getClassName () {
        return 'SongVoting';
    }

    /**
     * Create a new instance.
     */
    constructor () {
        /** @type {console} */
        this.logger = ClassLogger(this, true); // set second parameter to false to disable logging

        this.localStorageAutoplayKey = 'aby-songvoting-autoplay';

        if (window.navigationHandler) {
            this.addNavigationHandlerEvents();
        } else {
            window.addEventListener('navigationhandler:init', () => this.addNavigationHandlerEvents());
        }
    }

    /**
     * Listen vor navigation handler events to initialize this component.
     */
    addNavigationHandlerEvents () {
        window.navigationHandler.on('ready', () => this.init());
        window.navigationHandler.on('render', () => this.init());
    }

    /**
     * Initialize the song votings.
     */
    init () {
        this.logger.log('Initializing the song votings');
        this.initAutoPlay();

        const songVotingWrappers = document.querySelectorAll('[data-songvoting-wrapper]');

        if (songVotingWrappers.length === 0) {
            this.logger.log('No song voting wrappers found. Nothing to initialize.');
            return;
        }

        this.logger.log('Initializing the song voting wrappers', { songVotingWrappers });

        this.initImageSwing(songVotingWrappers);
    }

    /**
     * @param {NodeListOf<HTMLElement>} songVotingWrappers
     *
     * @returns {this}
     */
    initImageSwing (songVotingWrappers) {
        const config = {
            maxThrowOutDistance: 400,
            minThrowOutDistance: 100,
            isThrowOut: (_x, _y, element, confidence) => {
                const isThrowOut = confidence >= 1 && (this.overlapsVoteUp(element) || this.overlapsVoteDown(element));

                if (isThrowOut) {
                    this.onThrowout(element);
                }

                return isThrowOut;
            },
            throwOutConfidence: (xOffset, yOffset, element) => {
                const xConfidence = Math.min(Math.abs(xOffset) / element.offsetWidth * 1.5, 1);
                const yConfidence = Math.min(Math.abs(yOffset) / element.offsetHeight * 1.5, 1);

                const confidence = Math.max(xConfidence, yConfidence);

                // this.logger.log('throw out confidence', confidence);

                return confidence;
            },
        };

        const stack = new Stack(config);

        songVotingWrappers.forEach(wrapper => {
            stack.createCard(wrapper);
            wrapper.classList.add('in-deck');
        });

        return this;
    }

    /**
     * Initialize the autoplay checkbox: restore a previous state and add a click
     * listener to the checkbox which stores the new state in local storage.
     * Also, in situations we should autoplay a song: click the playbutton.
     */
    initAutoPlay () {
        this.logger.log('Initializing autoplay checkbox');

        /** @type {HTMLInputElement|null} */
        const autoplayCheckbox = document.querySelector('[data-songvoting-autoplay]');

        if (!autoplayCheckbox) {
            this.logger.log('Could not find autoplay checkbox. Ignoring autoplay');
            return;
        }

        autoplayCheckbox.checked = localStorage.getItem(this.localStorageAutoplayKey) === 'true';

        autoplayCheckbox.addEventListener('click', () => {
            localStorage.setItem(this.localStorageAutoplayKey, autoplayCheckbox.checked);
        });

        if (autoplayCheckbox.checked) {
            /** @type {HTMLButtonElement|null} */
            const playbutton = document.querySelector('[data-songvoting-playbutton]');

            if (playbutton) {
                /**
                 * This 100ms delay is needed when the song voting page is opened directly.
                 * In that case, the audio player initializes itself with the configured
                 * default channel and we also want to autoplay a song of this voting.
                 */
                setTimeout(() => playbutton.click(), 100);
            }
        }

        this.logger.log('Initialized autoplay checkbox');
    }

    /**
     * @param {HTMLElement} element
     */
    onThrowout (element) {
        this.logger.log('Card throwout detected');

        if (this.overlapsVoteUp(element)) {
            this.voteUp();
        } else if (this.overlapsVoteDown(element)) {
            this.voteDown();
        } else {
            this.logger.log('Neither vote up nor vote down buttons overlap on throwout');
        }
    }

    /**
     * @param {HTMLElement} candidate
     *
     * @returns {Boolean}
     */
    overlapsVoteUp (candidate) {
        const voteUpButton = this.voteUpButton();

        return voteUpButton != null && this.isOverlapping(
            voteUpButton.getBoundingClientRect(),
            candidate.getBoundingClientRect(),
        );
    }

    /**
     * @param {HTMLElement} candidate
     *
     * @returns {Boolean}
     */
    overlapsVoteDown (candidate) {
        const voteDownButton = this.voteDownButton();

        this.logger.log('overlaps down?', {
            candidate,
            isOverlapping: this.isOverlapping(
                voteDownButton.getBoundingClientRect(),
                candidate.getBoundingClientRect(),
            ),
        });

        return voteDownButton != null && this.isOverlapping(
            voteDownButton.getBoundingClientRect(),
            candidate.getBoundingClientRect(),
        );
    }

    /**
     * Determine whether the given rectangles `a` and `b  overlap.
     *
     * @param {DOMRect} a
     * @param {DOMRect} b
     *
     * @returns {Boolean}
     */
    isOverlapping (a, b) {
        // no horizontal overlap
        if (a.x >= b.x + b.width || b.x >= a.x + a.width) return false;

        // no vertical overlap
        if (a.y >= b.y + b.height || b.y >= a.y + a.height) return false;

        return true;
    }

    /**
     * Submit the "vote up" .
     */
    voteUp () {
        this.logger.log('Voting up 👍');

        /** @type {HTMLButtonElement|null} */
        const voteUpButton = this.voteUpButton();

        if (voteUpButton) {
            voteUpButton.click();
        }
    }

    /**
     * @returns {HTMLButtonElement|null}
     */
    voteUpButton () {
        return document.querySelector('[data-vote-up]');
    }

    /**
     * Submit the "vote down" .
     */
    voteDown () {
        this.logger.log('Voting down 👎');

        /** @type {HTMLButtonElement|null} */
        const voteDownButton = this.voteDownButton();

        if (voteDownButton) {
            voteDownButton.click();
        }
    }

    /**
     * @returns {HTMLButtonElement|null}
     */
    voteDownButton () {
        return document.querySelector('[data-vote-down]');
    }
}

new SongVoting();
